// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import { Alert, AppIcon, Button, Section, TextInput } from '@loa/ui-components';
import { TiptapEditor } from './TiptapEditor';

export interface InvitationEmailEditorProps {
  /**
   * Current subject line
   */
  subject: string;
  /**
   * Current body HTML
   */
  body: string;
  /**
   * Callback when subject changes
   */
  onSubjectChange: (subject: string) => void;
  /**
   * Callback when body changes
   */
  onBodyChange: (body: string) => void;
  /**
   * Whether the editor is disabled
   */
  disabled?: boolean;
  /**
   * Whether email has been modified from default
   */
  isModified?: boolean;
  /**
   * Callback to reset to default template
   */
  onReset?: () => void;
  /**
   * Subject line character limit
   */
  maxSubjectLength?: number;
  /**
   * Body character limit (HTML)
   */
  maxBodyLength?: number;
}

/**
 * InvitationEmailEditor - Email composition component with subject and body editing
 *
 * Features:
 * - Subject line input with character count
 * - Rich text body editor using Tiptap
 * - Reset to default template
 * - Validation feedback
 * - Accessible form controls
 */
export const InvitationEmailEditor = ({
  subject,
  body,
  onSubjectChange,
  onBodyChange,
  disabled = false,
  isModified = false,
  onReset,
  maxSubjectLength = 200,
  maxBodyLength = 10000,
}: InvitationEmailEditorProps) => {
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleSubjectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSubject = e.target.value;
      if (newSubject.length <= maxSubjectLength) {
        onSubjectChange(newSubject);
      }
    },
    [onSubjectChange, maxSubjectLength]
  );

  const handleBodyChange = useCallback(
    (html: string) => {
      if (html.length <= maxBodyLength) {
        onBodyChange(html);
      }
    },
    [onBodyChange, maxBodyLength]
  );

  const handleResetClick = useCallback(() => {
    setShowResetConfirm(true);
  }, []);

  const handleResetConfirm = useCallback(() => {
    onReset?.();
    setShowResetConfirm(false);
  }, [onReset]);

  const handleResetCancel = useCallback(() => {
    setShowResetConfirm(false);
  }, []);

  const subjectExceeded = subject.length > maxSubjectLength;

  return (
    <div>
      {/* Header with Reset Button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AppIcon name="mail" size="large" />
          <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>Email Content</span>
        </div>
        {onReset && isModified && (
          <Button
            type="button"
            variant="outlined"
            size="sm"
            onClick={handleResetClick}
            disabled={disabled}
            aria-label="Reset email to default template"
            iconLeft={<AppIcon name="refresh" />}
          >
            Reset to Default
          </Button>
        )}
      </div>

      {/* Reset Confirmation Message */}
      {showResetConfirm && (
        <Alert variant="warning" header="Reset to Default Template?">
          <p>This will discard your customizations and restore the default email content.</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="outlined" size="sm" onClick={handleResetCancel}>Cancel</Button>
            <Button variant="filled" colour="red" size="sm" onClick={handleResetConfirm}>
              Reset
            </Button>
          </div>
        </Alert>
      )}

      <Section>
        {/* Subject Line Input — uses shared Finerty TextInput so the label
            sits above a properly bordered, full-width input. The 200-char
            limit is still enforced inside handleSubjectChange. */}
        <TextInput
          id="email-subject"
          label="Subject Line"
          type="text"
          value={subject}
          onChange={handleSubjectChange}
          placeholder="Enter email subject..."
          disabled={disabled}
          fluid
          error={subjectExceeded ? 'Subject line exceeds maximum length' : undefined}
        />

        {/* Body Editor */}
        <div style={{ marginTop: '1rem' }}>
          <label
            htmlFor="email-body"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
            }}
          >
            Email Body
          </label>
          <TiptapEditor
            id="email-body"
            content={body}
            onChange={handleBodyChange}
            placeholder="Compose your invitation email..."
            disabled={disabled}
            minHeight="250px"
            maxHeight="400px"
            ariaLabel="Email body editor"
          />
        </div>
      </Section>
    </div>
  );
};

export default InvitationEmailEditor;
